import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from '@e965/xlsx';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Users, Mail,
  RefreshCw, Trash2, Search, Send, Clock, UserCheck, MailCheck, ArrowLeft,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useImportedAccounts } from '@/hooks/useImportedAccounts';
import { format } from 'date-fns';

const cleanHandle = (val: any): string | undefined => {
  const cleaned = (val || '').toString().replace(/[\t]/g, '').replace(/^@/, '').trim();
  return cleaned || undefined;
};

interface ParsedAccount {
  account_type: 'talent' | 'brand';
  full_name: string;
  email: string;
  phone?: string;
  talent_type?: string;
  sport?: string;
  team?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  x_handle?: string;
  threads?: string;
  athlete_level?: string;
  primary_platform?: string;
  music_genre?: string;
  label_status?: string;
  label_name?: string;
  entertainer_genres?: string;
  follower_counts?: string;
  company_name?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  hq_location?: string;
  title?: string;
  marketing_budget?: string;
  primary_goals?: string;
  target_audience?: string;
  errors: string[];
}

const AdminImportAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    accounts, loading, filters, setFilters, stats,
    resendEmail, resendAllPending, deleteImport, refetch,
  } = useImportedAccounts();

  // Upload state
  const [parsedAccounts, setParsedAccounts] = useState<ParsedAccount[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [detectedType, setDetectedType] = useState<'talent' | 'brand' | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const detectAccountType = (headers: string[]): 'talent' | 'brand' => {
    const lower = headers.map(h => h.toLowerCase());
    if (lower.includes('company_name') || lower.includes('hq_location') || lower.includes('marketing_budget')) return 'brand';
    return 'talent';
  };

  const revalidateRow = (account: ParsedAccount): ParsedAccount => {
    const errors: string[] = [];
    if (!(account.email || '').trim()) errors.push('Missing email');
    if (account.account_type === 'brand' && !(account.company_name || '').trim()) errors.push('Missing company name');
    return { ...account, errors };
  };

  const parseRow = (row: Record<string, any>, type: 'talent' | 'brand'): ParsedAccount => {
    const errors: string[] = [];
    const email = (row.email || '').toString().trim();
    if (!email) errors.push('Missing email');
    if (type === 'brand' && !(row.company_name || '').toString().trim()) errors.push('Missing company name');

    return {
      account_type: type,
      full_name: (row.full_name || '').toString().trim(),
      email,
      phone: (row.phone || '').toString().trim() || undefined,
      talent_type: (row.talent_type || '').toString().trim() || undefined,
      sport: (row.sport || '').toString().trim() || undefined,
      team: (row.team || '').toString().trim() || undefined,
      instagram: cleanHandle(row.instagram),
      tiktok: cleanHandle(row.tiktok),
      youtube: cleanHandle(row.youtube),
      x_handle: cleanHandle(row.x_handle),
      threads: cleanHandle(row.threads),
      athlete_level: (row.athlete_level || '').toString().trim() || undefined,
      primary_platform: (row.primary_platform || '').toString().trim().toLowerCase() || undefined,
      music_genre: (row.music_genre || '').toString().trim() || undefined,
      label_status: (row.label_status || '').toString().trim() || undefined,
      label_name: (row.label_name || '').toString().trim() || undefined,
      entertainer_genres: (row.entertainer_genres || '').toString().trim() || undefined,
      follower_counts: (row.follower_counts || '').toString().trim() || undefined,
      company_name: (row.company_name || '').toString().trim() || undefined,
      website: (row.website || '').toString().trim() || undefined,
      industry: (row.industry || '').toString().trim() || undefined,
      company_size: (row.company_size || '').toString().trim() || undefined,
      hq_location: (row.hq_location || '').toString().trim() || undefined,
      title: (row.title || '').toString().trim() || undefined,
      marketing_budget: (row.marketing_budget || '').toString().trim() || undefined,
      primary_goals: (row.primary_goals || '').toString().trim() || undefined,
      target_audience: (row.target_audience || '').toString().trim() || undefined,
      errors,
    };
  };

  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary', FS: ';' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (jsonData.length < 2) {
          toast({ title: 'Invalid file', description: 'Must have a header row and at least one data row.', variant: 'destructive' });
          return;
        }
        const headers = (jsonData[0] as string[]).map(h => h?.toString().trim() || '');
        const rows = jsonData.slice(1)
          .filter((row: any) => row.some((cell: any) => cell != null && cell !== ''))
          .map(row => {
            const obj: Record<string, any> = {};
            headers.forEach((header, i) => { obj[header] = (row as any[])[i]; });
            return obj;
          });
        const type = detectAccountType(headers);
        setDetectedType(type);
        const parsed = rows.map(r => parseRow(r, type));
        setParsedAccounts(parsed);
        // Auto-select all valid rows
        const validSet = new Set<number>();
        parsed.forEach((a, i) => { if (a.errors.length === 0) validSet.add(i); });
        setSelectedIndices(validSet);
        toast({ title: 'File loaded', description: `Found ${rows.length} ${type} accounts.` });
      } catch {
        toast({ title: 'Error parsing file', description: 'Please ensure the file is a valid CSV or Excel file.', variant: 'destructive' });
      }
    };
    reader.readAsBinaryString(file);
  }, [toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.name.match(/\.(xlsx|xls|csv)$/i)) handleFileUpload(file);
    else toast({ title: 'Invalid file type', description: 'Please upload a CSV or Excel file.', variant: 'destructive' });
  }, [handleFileUpload, toast]);

  const validAccounts = parsedAccounts.filter(a => a.errors.length === 0);

  const updateAccount = (index: number, field: keyof ParsedAccount, value: string) => {
    setParsedAccounts(prev => {
      const updated = [...prev];
      updated[index] = revalidateRow({ ...updated[index], [field]: value });
      return updated;
    });
  };

  const removeRow = (index: number) => {
    setParsedAccounts(prev => prev.filter((_, i) => i !== index));
    setSelectedIndices(prev => {
      const next = new Set<number>();
      prev.forEach(i => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  const toggleSelect = (index: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const validIndices = parsedAccounts
      .map((a, i) => (a.errors.length === 0 ? i : -1))
      .filter(i => i >= 0);
    const allSelected = validIndices.every(i => selectedIndices.has(i));
    if (allSelected) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(validIndices));
    }
  };

  const selectedValidCount = Array.from(selectedIndices).filter(
    i => parsedAccounts[i] && parsedAccounts[i].errors.length === 0
  ).length;

  const handleImport = async () => {
    const toImport = Array.from(selectedIndices)
      .filter(i => parsedAccounts[i]?.errors.length === 0)
      .map(i => parsedAccounts[i]);
    if (toImport.length === 0) return;
    setImporting(true);
    setImportProgress(10);
    try {
      const batchSize = 10;
      let total = { imported: 0, skipped: 0, emailsSent: 0, errors: [] as any[] };
      for (let i = 0; i < toImport.length; i += batchSize) {
        const batch = toImport.slice(i, i + batchSize).map(({ errors, ...rest }) => rest);
        const { data, error } = await supabase.functions.invoke('import-accounts', { body: { accounts: batch } });
        if (error) throw error;
        if (data) {
          total.imported += data.imported || 0;
          total.skipped += data.skipped || 0;
          total.emailsSent += data.emailsSent || 0;
          if (data.errors) total.errors.push(...data.errors);
        }
        setImportProgress(Math.min(95, ((i + batchSize) / toImport.length) * 85 + 10));
      }
      setImportProgress(100);
      toast({
        title: 'Import complete',
        description: `${total.imported} imported, ${total.emailsSent} emails sent, ${total.skipped} skipped.`,
      });
      setParsedAccounts([]);
      setSelectedIndices(new Set());
      setDetectedType(null);
      refetch();
    } catch (error: any) {
      toast({ title: 'Import failed', description: error.message, variant: 'destructive' });
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };
  const handleResend = async (id: string) => {
    setResendingId(id);
    await resendEmail(id);
    setResendingId(null);
  };

  return (
    <Layout>
      <PageHeader title="Import Accounts" subtitle="Bulk import talent and brand accounts from CSV files and manage imported accounts" />
      
      <div className="p-4 md:p-6 space-y-6 max-w-[1200px] mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Profile
        </Button>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <div className="text-2xl font-bold text-foreground">{stats.total}</div>
                <div className="text-xs text-muted-foreground">Total Imported</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <UserCheck className="h-8 w-8 text-primary" />
              <div>
                <div className="text-2xl font-bold text-foreground">{stats.claimed}</div>
                <div className="text-xs text-muted-foreground">Claimed</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="h-8 w-8 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold text-foreground">{stats.pending}</div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <MailCheck className="h-8 w-8 text-primary" />
              <div>
                <div className="text-2xl font-bold text-foreground">{stats.emailsSent}</div>
                <div className="text-xs text-muted-foreground">Emails Sent</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CSV Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload CSV
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {parsedAccounts.length === 0 ? (
              <div
                className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <h3 className="text-sm font-semibold mb-1">Drop CSV file here</h3>
                <p className="text-xs text-muted-foreground mb-3">Supports semicolon-delimited CSV files from talent or brand signups</p>
                <label>
                  <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }} />
                  <Button variant="outline" size="sm" asChild><span><FileSpreadsheet className="h-4 w-4 mr-2" />Browse Files</span></Button>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={detectedType === 'talent' ? 'default' : 'secondary'}>
                      {detectedType === 'talent' ? 'Talent' : 'Brand'} CSV
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {selectedValidCount} of {validAccounts.length} selected · {parsedAccounts.length - validAccounts.length} with errors
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setParsedAccounts([]); setSelectedIndices(new Set()); setDetectedType(null); }}>Clear</Button>
                    <Button size="sm" onClick={handleImport} disabled={selectedValidCount === 0 || importing}>
                      {importing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                      Import {selectedValidCount} Accounts
                    </Button>
                  </div>
                </div>

                {importing && <Progress value={importProgress} className="h-2" />}

                <ScrollArea className="h-[350px] border rounded-lg">
                  <Table className="min-w-[2200px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={validAccounts.length > 0 && validAccounts.length === selectedValidCount}
                            onCheckedChange={toggleSelectAll}
                          />
                        </TableHead>
                        <TableHead className="w-8">#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        {detectedType === 'talent' && (
                          <>
                            <TableHead>Talent Type</TableHead>
                            <TableHead>Sport</TableHead>
                            <TableHead>Team</TableHead>
                            <TableHead>Athlete Level</TableHead>
                            <TableHead>Primary Platform</TableHead>
                            <TableHead>Instagram</TableHead>
                            <TableHead>TikTok</TableHead>
                            <TableHead>YouTube</TableHead>
                            <TableHead>Threads</TableHead>
                            <TableHead>X Handle</TableHead>
                            <TableHead>Music Genre</TableHead>
                            <TableHead>Label Status</TableHead>
                            <TableHead>Label Name</TableHead>
                            <TableHead>Entertainer Genres</TableHead>
                            <TableHead>Follower Counts</TableHead>
                          </>
                        )}
                        {detectedType === 'brand' && (
                          <>
                            <TableHead>Company</TableHead>
                            <TableHead>Website</TableHead>
                            <TableHead>Industry</TableHead>
                            <TableHead>Company Size</TableHead>
                            <TableHead>HQ Location</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Marketing Budget</TableHead>
                            <TableHead>Target Audience</TableHead>
                          </>
                        )}
                        <TableHead>Status</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedAccounts.map((account, i) => {
                        const hasErrors = account.errors.length > 0;
                        return (
                          <TableRow key={i} className={hasErrors ? 'bg-destructive/5' : selectedIndices.has(i) ? 'bg-primary/5' : ''}>
                            <TableCell>
                              <Checkbox
                                checked={selectedIndices.has(i)}
                                onCheckedChange={() => toggleSelect(i)}
                                disabled={hasErrors}
                              />
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                            <TableCell>
                              <Input className="h-7 text-xs min-w-[120px]" value={account.full_name} onChange={(e) => updateAccount(i, 'full_name', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <Input className="h-7 text-xs min-w-[160px]" value={account.email} onChange={(e) => updateAccount(i, 'email', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <Input className="h-7 text-xs min-w-[110px]" value={account.phone || ''} onChange={(e) => updateAccount(i, 'phone', e.target.value)} />
                            </TableCell>
                            {detectedType === 'talent' && (
                              <>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.talent_type || ''} onChange={(e) => updateAccount(i, 'talent_type', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[90px]" value={account.sport || ''} onChange={(e) => updateAccount(i, 'sport', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.team || ''} onChange={(e) => updateAccount(i, 'team', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.athlete_level || ''} onChange={(e) => updateAccount(i, 'athlete_level', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.primary_platform || ''} onChange={(e) => updateAccount(i, 'primary_platform', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.instagram || ''} onChange={(e) => updateAccount(i, 'instagram', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.tiktok || ''} onChange={(e) => updateAccount(i, 'tiktok', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.youtube || ''} onChange={(e) => updateAccount(i, 'youtube', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.threads || ''} onChange={(e) => updateAccount(i, 'threads', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.x_handle || ''} onChange={(e) => updateAccount(i, 'x_handle', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.music_genre || ''} onChange={(e) => updateAccount(i, 'music_genre', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.label_status || ''} onChange={(e) => updateAccount(i, 'label_status', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.label_name || ''} onChange={(e) => updateAccount(i, 'label_name', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.entertainer_genres || ''} onChange={(e) => updateAccount(i, 'entertainer_genres', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.follower_counts || ''} onChange={(e) => updateAccount(i, 'follower_counts', e.target.value)} /></TableCell>
                              </>
                            )}
                            {detectedType === 'brand' && (
                              <>
                                <TableCell><Input className="h-7 text-xs min-w-[120px]" value={account.company_name || ''} onChange={(e) => updateAccount(i, 'company_name', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[120px]" value={account.website || ''} onChange={(e) => updateAccount(i, 'website', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.industry || ''} onChange={(e) => updateAccount(i, 'industry', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.company_size || ''} onChange={(e) => updateAccount(i, 'company_size', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.hq_location || ''} onChange={(e) => updateAccount(i, 'hq_location', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.title || ''} onChange={(e) => updateAccount(i, 'title', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.marketing_budget || ''} onChange={(e) => updateAccount(i, 'marketing_budget', e.target.value)} /></TableCell>
                                <TableCell><Input className="h-7 text-xs min-w-[100px]" value={account.target_audience || ''} onChange={(e) => updateAccount(i, 'target_audience', e.target.value)} /></TableCell>
                              </>
                            )}
                            <TableCell>
                              {hasErrors ? (
                                <span className="flex items-center gap-1 text-destructive text-xs whitespace-nowrap">
                                  <AlertCircle className="h-3 w-3" />{account.errors[0]}
                                </span>
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => removeRow(i)} className="h-7 w-7 p-0 text-destructive hover:text-destructive">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Imported Accounts Section */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Imported Accounts
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={resendAllPending} disabled={stats.pending === 0}>
                  <Send className="h-3 w-3 mr-1" />
                  Resend All Pending
                </Button>
                <Button variant="ghost" size="sm" onClick={refetch}>
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  className="pl-9"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v as any }))}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="claimed">Claimed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.type} onValueChange={(v) => setFilters(prev => ({ ...prev, type: v as any }))}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="talent">Talent</SelectItem>
                  <SelectItem value="brand">Brand</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
            ) : accounts.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No imported accounts yet. Upload a CSV above to get started.
              </div>
            ) : (
              <ScrollArea className="border rounded-lg">
                <Table className="min-w-[2400px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Sub-type</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Sport</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead>Instagram</TableHead>
                      <TableHead>TikTok</TableHead>
                      <TableHead>YouTube</TableHead>
                      <TableHead>X</TableHead>
                      <TableHead>Threads</TableHead>
                      <TableHead>Music Genre</TableHead>
                      <TableHead>Label Status</TableHead>
                      <TableHead>Entertainer Genres</TableHead>
                      <TableHead>Imported</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Email</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className="font-medium text-sm">{account.full_name || '—'}</TableCell>
                        <TableCell className="text-sm">{account.email}</TableCell>
                        <TableCell>
                          <Badge variant={account.account_type === 'talent' ? 'default' : 'secondary'} className="text-xs">
                            {account.account_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{account.talent_type || '—'}</TableCell>
                        <TableCell className="text-sm">{account.phone || '—'}</TableCell>
                        <TableCell className="text-sm">{account.sport || '—'}</TableCell>
                        <TableCell className="text-sm">{account.team_name || '—'}</TableCell>
                        <TableCell className="text-sm">{account.athlete_level || '—'}</TableCell>
                        <TableCell className="text-sm">{account.instagram || '—'}</TableCell>
                        <TableCell className="text-sm">{account.tiktok || '—'}</TableCell>
                        <TableCell className="text-sm">{account.youtube || '—'}</TableCell>
                        <TableCell className="text-sm">{account.x_handle || '—'}</TableCell>
                        <TableCell className="text-sm">{account.threads || '—'}</TableCell>
                        <TableCell className="text-sm">{account.music_genre || '—'}</TableCell>
                        <TableCell className="text-sm">{account.label_status || '—'}</TableCell>
                        <TableCell className="text-sm">{account.entertainer_genres || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(account.imported_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={account.claim_status === 'claimed' ? 'default' : 'outline'}
                            className={`text-xs ${account.claim_status === 'claimed' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                          >
                            {account.claim_status === 'claimed' ? 'Claimed' : 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {account.last_email_sent_at
                            ? format(new Date(account.last_email_sent_at), 'MMM d, h:mm a')
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {account.claim_status !== 'claimed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResend(account.id)}
                                disabled={resendingId === account.id}
                              >
                                {resendingId === account.id ? (
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Mail className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteImport(account.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminImportAccountsPage;
